import { useState } from "react";
import FORMS from "../../api/services/FORMS";
import FormPreviewModal from "./formPreviewModal";
import styles from "../../styles/_containers/_forms/formBuilder.module.scss";

const QUESTION_TYPES = [
  { value: "short_text", label: "טקסט קצר" },
  { value: "long_text", label: "טקסט ארוך" },
  { value: "date", label: "תאריך" },
  { value: "checkbox", label: "תיבת סימון" },
  { value: "yes_no", label: "כן / לא" },
  { value: "single_choice", label: "בחירה יחידה" },
  { value: "multi_choice", label: "בחירה מרובה" },
  { value: "rating", label: "דירוג 1-5" },
];

const emptyQuestion = () => ({
  id: `q_${Date.now()}_${Math.random()}`,
  text: "",
  type: "short_text",
  required: false,
  options: [""],
  order: 0,
  alertOn: [],
});

// Per-question "alert on answer" picker. Identical semantics to
// the RN AlertOnPicker — marks specific answer values as sensitive
// so the system fires a push + red-flags the submission.
const AlertOnPicker = ({ question, onChange }) => {
  const alertOn = Array.isArray(question?.alertOn) ? question.alertOn : [];
  const hasAny = alertOn.length > 0;
  const isOn = (v) =>
    alertOn.some((x) => String(x).toLowerCase() === String(v).toLowerCase());
  const toggle = (v) => {
    const low = String(v).toLowerCase();
    onChange(
      alertOn.some((x) => String(x).toLowerCase() === low)
        ? alertOn.filter((x) => String(x).toLowerCase() !== low)
        : [...alertOn, v]
    );
  };
  const chip = (v, label) => (
    <button
      type="button"
      key={v}
      onClick={() => toggle(v)}
      style={{
        borderRadius: 14,
        padding: "3px 10px",
        border: `1px solid ${isOn(v) ? "#dc2626" : "#e2e8f0"}`,
        background: isOn(v) ? "#fef2f2" : "#fff",
        color: isOn(v) ? "#dc2626" : "#64748b",
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        marginInlineEnd: 6,
        marginBottom: 6,
        fontFamily: "inherit",
      }}
    >
      {isOn(v) ? "⚠️ " : ""}
      {label}
    </button>
  );
  return (
    <div
      style={{
        marginTop: 8,
        paddingTop: 8,
        borderTop: `1px solid ${hasAny ? "#fecaca" : "#f1f5f9"}`,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: hasAny ? "#dc2626" : "#64748b",
          fontWeight: 600,
          marginBottom: 6,
          textAlign: "right",
        }}
      >
        ⚠️ התראה על תשובה
        {hasAny ? ` (${alertOn.length} מוגדרות)` : " (אופציונלי)"}
      </div>
      {question.type === "yes_no" ? (
        <div style={{ display: "flex", flexDirection: "row-reverse", flexWrap: "wrap" }}>
          {chip("yes", 'התראה על "כן"')}
          {chip("no", 'התראה על "לא"')}
        </div>
      ) : ["single_choice", "multi_choice"].includes(question.type) ? (
        <div style={{ display: "flex", flexDirection: "row-reverse", flexWrap: "wrap" }}>
          {(question.options || []).filter(Boolean).map((opt) => chip(opt, opt))}
        </div>
      ) : (
        <input
          type="text"
          value={alertOn.join(", ")}
          onChange={(e) =>
            onChange(
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          placeholder="ערכים להתראה (מופרדים בפסיק)"
          dir="rtl"
          style={{
            width: "100%",
            border: `1px solid ${hasAny ? "#fecaca" : "#e2e8f0"}`,
            borderRadius: 6,
            padding: "6px 8px",
            fontSize: 12,
            color: "#111",
            fontFamily: "inherit",
            textAlign: "right",
          }}
        />
      )}
    </div>
  );
};

const FormBuilder = ({ type, initialData, onClose, onSaved }) => {
  const isEdit = !!(initialData?._id);
  const isFromTemplate = !!(initialData?._templateId); // new from built-in template
  const isDeclaration = type === "declaration";

  const [title, setTitle] = useState(initialData?.title || "");
  const [content, setContent] = useState(initialData?.content || "");
  // Questionnaire-only intro/outro text. Shown before/after the
  // question list on the client-facing form. Falls back to the
  // template's legacy `content` for built-in templates that still
  // stashed their preamble there.
  const [introText, setIntroText] = useState(
    initialData?.introText ||
      (type === "questionnaire" ? initialData?.content || "" : "")
  );
  const [outroText, setOutroText] = useState(initialData?.outroText || "");
  const [questions, setQuestions] = useState(
    initialData?.questions?.length ? initialData.questions : [emptyQuestion()]
  );
  const [hasSignature, setHasSignature] = useState(initialData?.hasSignature || false);
  const [signatureType, setSignatureType] = useState(initialData?.signatureType || "checkbox");
  const [includeClientName, setIncludeClientName] = useState(initialData?.includeClientName || false);
  const [includeClientPhone, setIncludeClientPhone] = useState(initialData?.includeClientPhone || false);
  const [requiresResubmission, setRequiresResubmission] = useState(initialData?.requiresResubmission || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const insertToken = (token) => {
    setContent((c) => c + " " + token);
  };

  const addQuestion = () => {
    setQuestions((prev) => [...prev, { ...emptyQuestion(), order: prev.length }]);
  };

  const removeQuestion = (idx) => {
    setQuestions((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx, field, value) => {
    setQuestions((prev) => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  };

  // Adjacent-swap reorder so admins can fix question order without
  // deleting and re-adding. Guarded at the edges.
  const moveQuestion = (idx, direction) => {
    setQuestions((prev) => {
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const addOption = (idx) => {
    setQuestions((prev) => prev.map((q, i) => i === idx ? { ...q, options: [...q.options, ""] } : q));
  };

  const updateOption = (qIdx, oIdx, value) => {
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...q.options];
      opts[oIdx] = value;
      return { ...q, options: opts };
    }));
  };

  const removeOption = (qIdx, oIdx) => {
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== qIdx) return q;
      return { ...q, options: q.options.filter((_, j) => j !== oIdx) };
    }));
  };

  const handleSave = async () => {
    if (!title.trim()) { setError("יש להזין כותרת"); return; }
    if (isDeclaration && !content.trim()) { setError("יש להזין תוכן הטופס"); return; }
    if (!isDeclaration && questions.some((q) => !q.text.trim())) { setError("יש למלא את כל שאלות"); return; }
    setError("");
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        type,
        content: isDeclaration ? content : "",
        introText: isDeclaration ? "" : introText || "",
        outroText: isDeclaration ? "" : outroText || "",
        questions: isDeclaration
          ? []
          : questions.map((q, i) => ({
              ...q,
              order: i,
              options: q.options.filter(Boolean),
              alertOn: Array.isArray(q.alertOn) ? q.alertOn : [],
            })),
        hasSignature,
        signatureType,
        includeClientName,
        includeClientPhone,
        requiresResubmission,
        isBuiltIn: isFromTemplate && !isEdit, // true only when first created from template
      };
      const res = isEdit
        ? await FORMS.update(initialData._id, payload)
        : await FORMS.create(payload);
      onSaved(res.data?.data || res.data);
    } catch (e) {
      setError(e?.response?.data?.message || "שגיאה בשמירה");
    } finally {
      setSaving(false);
    }
  };

  const needsOptions = (t) => ["single_choice", "multi_choice"].includes(t);

  const previewData = {
    title: title.trim() || (isDeclaration ? "טופס חדש" : "שאלון חדש"),
    type,
    content,
    introText,
    outroText,
    questions,
    hasSignature,
    signatureType,
    includeClientName,
    includeClientPhone,
  };

  return (
    <>
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div><span>{isEdit ? "עריכת מסמך" : "מסמך חדש"}</span><h2>{isEdit ? "עריכת" : "יצירת"} {isDeclaration ? "טופס" : "שאלון"}</h2></div>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          {/* Title */}
          <div className={styles.fieldGroup}>
            <label className={styles.label}>שם {isDeclaration ? "הטופס" : "השאלון"} <span className={styles.req}>*</span></label>
            <input
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={isDeclaration ? "לדוגמה: טופס ויתור וסודיות" : "לדוגמה: שאלון היכרות ראשוני"}
              dir="rtl"
            />
          </div>

          {/* Client fields tokens */}
          <div className={styles.tokensRow}>
            <label className={styles.labelSm}>שדות לקוח אוטומטיים:</label>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={includeClientName} onChange={(e) => setIncludeClientName(e.target.checked)} />
              שם מלא
            </label>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={includeClientPhone} onChange={(e) => setIncludeClientPhone(e.target.checked)} />
              טלפון
            </label>
          </div>

          {/* DECLARATION content */}
          {isDeclaration && (
            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>תוכן הטופס <span className={styles.req}>*</span></label>
                <div className={styles.tokenBtns}>
                  <button type="button" className={styles.tokenBtn} onClick={() => insertToken("{{שם_לקוח}}")}>+ שם לקוח</button>
                  <button type="button" className={styles.tokenBtn} onClick={() => insertToken("{{טלפון}}")}>+ טלפון</button>
                  <button type="button" className={styles.tokenBtn} onClick={() => insertToken("{{תאריך}}")}>+ תאריך</button>
                </div>
              </div>
              <textarea
                className={styles.textarea}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="הכנס את תוכן הטופס כאן... ניתן להשתמש בשדות דינמיים כמו {{שם_לקוח}}"
                rows={10}
                dir="rtl"
              />
            </div>
          )}

          {/* QUESTIONNAIRE questions */}
          {!isDeclaration && (
            <div className={styles.fieldGroup}>
              {/* Intro text — shown before the question list on the
                  client-facing form. Optional but useful for
                  disclaimers / "why we're asking" context. */}
              <label className={styles.label}>מבוא / הסבר לפני השאלות (אופציונלי)</label>
              <textarea
                className={styles.textarea}
                value={introText}
                onChange={(e) => setIntroText(e.target.value)}
                placeholder="טקסט שיופיע בראש השאלון, לפני השאלות..."
                rows={4}
                dir="rtl"
                style={{ marginBottom: 16 }}
              />

              <label className={styles.label}>שאלות השאלון <span className={styles.req}>*</span></label>
              <div className={styles.questionsList}>
                {questions.map((q, idx) => (
                  <div key={q.id || idx} className={styles.questionCard}>
                    <div className={styles.questionTop}>
                      <span className={styles.qNum}>{idx + 1}</span>
                      <input
                        className={styles.qInput}
                        value={q.text}
                        onChange={(e) => updateQuestion(idx, "text", e.target.value)}
                        placeholder="הכנס שאלה"
                        dir="rtl"
                      />
                      <select
                        className={styles.typeSelect}
                        value={q.type}
                        onChange={(e) => updateQuestion(idx, "type", e.target.value)}
                      >
                        {QUESTION_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <label className={styles.reqCheck}>
                        <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(idx, "required", e.target.checked)} />
                        חובה
                      </label>
                      {/* Reorder buttons — simple up/down so admins
                          can fix question order without delete + re-add */}
                      <button
                        type="button"
                        onClick={() => moveQuestion(idx, -1)}
                        disabled={idx === 0}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: idx === 0 ? "not-allowed" : "pointer",
                          opacity: idx === 0 ? 0.3 : 1,
                          fontSize: 16,
                          color: "#64748b",
                          padding: "2px 6px",
                        }}
                        title="הזז למעלה"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveQuestion(idx, +1)}
                        disabled={idx === questions.length - 1}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: idx === questions.length - 1 ? "not-allowed" : "pointer",
                          opacity: idx === questions.length - 1 ? 0.3 : 1,
                          fontSize: 16,
                          color: "#64748b",
                          padding: "2px 6px",
                        }}
                        title="הזז למטה"
                      >
                        ↓
                      </button>
                      {questions.length > 1 && (
                        <button className={styles.removeQ} onClick={() => removeQuestion(idx)}>🗑</button>
                      )}
                    </div>

                    {/* Options for choice types */}
                    {needsOptions(q.type) && (
                      <div className={styles.optionsList}>
                        {(q.options || []).map((opt, oIdx) => (
                          <div key={oIdx} className={styles.optionRow}>
                            <span className={styles.optBullet}>•</span>
                            <input
                              className={styles.optInput}
                              value={opt}
                              onChange={(e) => updateOption(idx, oIdx, e.target.value)}
                              placeholder={`אפשרות ${oIdx + 1}`}
                              dir="rtl"
                            />
                            {(q.options || []).length > 1 && (
                              <button className={styles.removeOpt} onClick={() => removeOption(idx, oIdx)}>✕</button>
                            )}
                          </div>
                        ))}
                        <button className={styles.addOptBtn} onClick={() => addOption(idx)}>+ הוסף אפשרות</button>
                      </div>
                    )}

                    {/* Sensitive-answer alert configuration */}
                    <AlertOnPicker
                      question={q}
                      onChange={(vals) => updateQuestion(idx, "alertOn", vals)}
                    />
                  </div>
                ))}
              </div>
              <button className={styles.addQBtn} onClick={addQuestion}>+ הוסף שאלה</button>

              {/* Outro text — optional closing note shown after the
                  question list. */}
              <label className={styles.label} style={{ marginTop: 16 }}>
                סיכום / הערה אחרי השאלות (אופציונלי)
              </label>
              <textarea
                className={styles.textarea}
                value={outroText}
                onChange={(e) => setOutroText(e.target.value)}
                placeholder="טקסט שיופיע בסוף השאלון, אחרי השאלות (למשל: הצהרת סודיות)..."
                rows={4}
                dir="rtl"
              />
            </div>
          )}

          {/* Signature section */}
          <div className={styles.sigSection}>
            <label className={styles.checkRow}>
              <input type="checkbox" checked={hasSignature} onChange={(e) => setHasSignature(e.target.checked)} />
              <span className={styles.sigLabel}>הוסף שדה חתימה</span>
            </label>
            {hasSignature && (
              <div className={styles.sigOptions}>
                <label className={styles.radioRow}>
                  <input type="radio" name="sigType" value="checkbox" checked={signatureType === "checkbox"} onChange={() => setSignatureType("checkbox")} />
                  ☑️ "קראתי ואני מסכים/ה"
                </label>
                <label className={styles.radioRow}>
                  <input type="radio" name="sigType" value="draw" checked={signatureType === "draw"} onChange={() => setSignatureType("draw")} />
                  ✍️ חתימת יד (קשקוש על המסך)
                </label>
                <label className={styles.radioRow}>
                  <input type="radio" name="sigType" value="both" checked={signatureType === "both"} onChange={() => setSignatureType("both")} />
                  שניהם
                </label>
              </div>
            )}
          </div>

          {/* Resubmission */}
          <label className={styles.checkRow}>
            <input type="checkbox" checked={requiresResubmission} onChange={(e) => setRequiresResubmission(e.target.checked)} />
            נדרשת חתימה חוזרת בכל ביקור
          </label>

          {error && <p className={styles.error}>{error}</p>}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>ביטול</button>
          <button className={styles.previewBtn} type="button" onClick={() => setPreviewOpen(true)}>תצוגה מקדימה</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? "שומר..." : isEdit ? "שמור שינויים" : `צור ${isDeclaration ? "טופס" : "שאלון"}`}
          </button>
        </div>
      </div>
    </div>
    {previewOpen && <FormPreviewModal form={previewData} onClose={() => setPreviewOpen(false)} />}
    </>
  );
};

export default FormBuilder;
